import { InfoCircleOutlined } from '@ant-design/icons';
import { Table, Tag, Tooltip, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { createSearchParams, Link, Outlet, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import { withRouteChange } from '../../services/APM';
import { GroupCollection } from '../../types';
import { buildPermissionsMap } from '../../utils';

const AccessProfiles = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = useAPIQuery(
    [
      '/permissions/',
      {
        params: {
          codename__in: [
            'view_all_vacancies',
            'create_and_edit_vacancies',
            'access_talent_pool',
            'access_configurations',
            'access_reports',
            'view_marked_request_job_postings',
            'view_all_request_job_postings',
            'create_and_edit_request_job_postings',
            'can_approve_request_job_postings',
          ].join(','),
        },
      },
    ],
    {
      select(data) {
        return buildPermissionsMap(data);
      },
    }
  );

  const groups = useAPIQuery(
    [
      '/groups/',
      {
        params: {
          page: parseInt(searchParams.get('page') || '1', 10),
          page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        },
      },
    ],
    {
      keepPreviousData: true,
    }
  );

  return (
    <>
      <Helmet>
        <title>Perfis | Configurações | Recrutamento e Seleção</title>
      </Helmet>
      <Outlet />
      <Table
        rowKey="id"
        dataSource={groups.data?.results}
        loading={groups.isFetching || permissions.isFetching}
        pagination={{
          showTotal: (total) => {
            return `Total: ${total} perfis`;
          },
          total: groups.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
      >
        <Table.Column
          title="Perfil de acesso"
          render={(record: GroupCollection) =>
            record.name === 'Padrão' ? (
              <Typography.Text>
                {record.name}{' '}
                <Tooltip title="Este perfil é aplicado automaticamente a novos usuários do sistema. Se precisar de permissões diferentes, crie um novo perfil com as configurações desejadas e atribua ao usuário.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Typography.Text>
            ) : (
              <Link
                to={{
                  pathname: `${record.id}/change`,
                  search: createSearchParams(searchParams).toString(),
                }}
              >
                {record.name}
              </Link>
            )
          }
        />
        <Table.Column
          title="Permissões"
          render={(record: GroupCollection) => (
            <>
              {permissions.data &&
              record.permissions?.includes(permissions.data.view_all_vacancies) ? (
                <Tag>Visualizar todas as vagas</Tag>
              ) : (
                <Tag>Visualizar apenas vagas marcadas</Tag>
              )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.create_and_edit_vacancies) && (
                  <Tag>Criar e editar vagas</Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.access_talent_pool) && (
                  <Tag>Acesso ao Banco de Talentos</Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.access_configurations) && (
                  <Tag>Acesso às Configurações</Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.access_reports) && (
                  <Tag>Acesso aos Relatórios</Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.view_marked_request_job_postings) && (
                  <Tag>Visualizar apenas as requisições marcadas</Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(permissions.data.view_all_request_job_postings) && (
                  <Tag>Visualizar todas as requisições</Tag>
                )}
              {permissions.data &&
                !record.permissions.includes(permissions.data.view_marked_request_job_postings) &&
                permissions.data &&
                !record.permissions?.includes(permissions.data.view_all_request_job_postings) && (
                  <Tag>Sem acesso a requisições</Tag>
                )}
              {permissions.data &&
                record.permissions?.includes(
                  permissions.data.create_and_edit_request_job_postings
                ) && <Tag>Criação e Edição de requisições</Tag>}
              {permissions.data &&
                record.permissions?.includes(permissions.data.can_approve_request_job_postings) && (
                  <Tag>Aprovar requisições</Tag>
                )}
            </>
          )}
        />
      </Table>
    </>
  );
};

export default withRouteChange()(AccessProfiles);
